import React, { useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import combinedCardData from '../common/Cardsdata';
import bankData from '../common/BankData';
import Consultation from './Consultation';
import useSeo from '../hooks/useSeo';
import { AuthContext } from '../context/AuthContext';

export default function BankDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const bank = location.state?.bank;

  const [expandedCard, setExpandedCard] = useState(null);
  const { user } = useContext(AuthContext);

  useSeo({
    title: bank
      ? `${bank.name} Credit Cards | CardAcademy`
      : 'Bank Details | CardAcademy',
    description: bank
      ? `Explore premium credit cards offered by ${bank.name}, including rewards, fees, and benefits.`
      : 'Explore partner banks and credit card options.',
    canonical: window.location.href,
  });

  if (!bank)
    return (
      <p style={{ padding: '2rem', color: 'red' }}>Bank details not found.</p>
    );

  const filteredCards = combinedCardData.filter(
    (card) => card.bank === bank.name
  );
  const otherPopularBanks = bankData
    .filter((b) => b.name !== bank.name)
    .slice(0, 3);

  const getBankLogo = (bankName) => {
    const match = bankData.find((b) => b.name === bankName);
    return match ? match.logo : '';
  };

  const goToBank = (bank) => {
    const slug = bank.name.toLowerCase().replace(/\s+/g, '-');
    navigate(`/partner-banks/${slug}`, { state: { bank } });
  };

  const handleCardClick = (card) => {
    navigate('/credit-cards/card-blog', { state: card });
  };

  // If user not logged in, send to /login, otherwise go to card
  const handleProtectedClick = (card) => {
    if (!user) {
      navigate('/login');
    } else {
      handleCardClick(card);
    }
  };

  const isMobile = window.innerWidth <= 768;

  const bankLogoSrc = bank.logo || getBankLogo(bank.name);

  const cardWidth = isMobile ? '340px' : '310px';
  const cardHeight = isMobile ? '570px' : '570px';

  const getTitleStyle = (title) => {
    const len = (title || '').length;
    if (isMobile) {
      const fontSize = len > 50 ? '0.95rem' : len > 35 ? '1rem' : '1.05rem';
      return {
        fontSize,
        fontWeight: 600,
        marginBottom: '0.25rem',
        lineHeight: '1.15',
        wordBreak: 'break-word',
        overflow: 'hidden',
      };
    }
    const fontSize = len > 70 ? '0.95rem' : len > 45 ? '1rem' : '1.05rem';
    return {
      fontSize,
      fontWeight: 600,
      marginBottom: '0.25rem',
      lineHeight: '1.12',
      wordBreak: 'break-word',
      overflow: 'hidden',
    };
  };

  const getHeaderTitleStyle = (title) => {
    const len = (title || '').length;
    if (isMobile) {
      return {
        fontSize: len > 25 ? '1.05rem' : '1.2rem',
        fontWeight: 600,
        marginBottom: '0.5rem',
        wordBreak: 'break-word',
      };
    }
    return {
      fontSize: len > 30 ? '1.15rem' : '1.3rem',
      fontWeight: 600,
      marginBottom: '0.5rem',
      wordBreak: 'break-word',
    };
  };

  const dividerStyle = {
    borderBottom: '1px solid #e5e7eb',
    margin: '0 1rem',
    marginBottom: '20px',
  };

  const backLinkStyle = {
    cursor: 'pointer',
    color: '#000',
    fontWeight: 600,
    fontSize: isMobile ? '1.1rem' : '1.3rem',
    marginTop: isMobile ? '24px' : '60px',
    marginBottom: isMobile ? '24px' : '40px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: isMobile ? '8px 0' : 0,
  };

  return (
    <div
      style={{
        padding: isMobile ? '1.5rem 1rem' : '2rem',
        fontFamily: 'Titillium Web, sans-serif',
        backgroundColor: '#f2f6ff',
      }}
    >
      <div style={backLinkStyle} onClick={() => navigate(-1)}>
        ← Back to Banks
      </div>

      {/* Top Section */}
      <div
        style={{
          background: 'linear-gradient(90deg, #004AAD, #1E90FF)',
          borderRadius: '10px',
          padding: isMobile ? '2rem 1.5rem' : '3rem 2rem',
          color: '#fff',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          gap: '2rem',
          marginBottom: '6rem',
          minHeight: isMobile ? 'auto' : '250px',
          textAlign: isMobile ? 'center' : 'left',
        }}
      >
        <div
          style={{
            width: isMobile ? '120px' : '160px',
            height: isMobile ? '120px' : '160px',
            borderRadius: '10px',
            padding: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: isMobile ? '0 auto -2rem' : '0',
            background: '#fff',
          }}
        >
          <img
            src={bankLogoSrc}
            alt={bank.name}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <h1 style={getHeaderTitleStyle(bank.name)}>{bank.name}</h1>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
            About {bank.name}
          </p>
          <p style={{ fontSize: '1rem', margin: 0 }}>
            {bank.name} most premium cards and offer maximum returns.
          </p>
        </div>
      </div>

      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '2rem' }}>
        {bank.name} Credit Cards
      </h2>

      {/* Cards Section */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '2.5rem',
          justifyContent: isMobile ? 'center' : 'flex-start',
          alignItems: 'flex-start',
        }}
      >
        {filteredCards.length === 0 && (
          <p style={{ fontSize: '1rem', color: '#555' }}>
            No card available of this bank
          </p>
        )}
        {filteredCards.map((card, index) => {
          const isExpanded = expandedCard === card.id;
          const truncated = card.description.slice(0, 100);
          const gradientStyle =
            index % 3 === 0
              ? 'linear-gradient(135deg, #FFF4CE, #ffffff)'
              : index % 3 === 1
                ? 'linear-gradient(135deg, #F9DEDC, #ffffff)'
                : 'linear-gradient(135deg, #EADDFF, #ffffff)';

          return (
            <div
              key={card.id || index}
              onClick={() => handleProtectedClick(card)}
              style={{
                width: isMobile ? cardWidth : '310px',
                height: cardHeight,
                borderRadius: '16px',
                background: gradientStyle,
                overflow: 'hidden',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: isMobile ? 'space-between' : 'flex-start',
                transition: 'transform 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (!isMobile) e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                if (!isMobile) e.currentTarget.style.transform = 'none';
              }}
            >
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ flex: 1, paddingRight: '0.5rem' }}>
                  <h3 style={getTitleStyle(card.creditcardname)}>
                    {card.creditcardname}
                  </h3>
                  <div style={{ fontSize: '0.9rem' }}>
                    <span style={{ color: '#FFD700' }}>★</span>{' '}
                    <span style={{ color: '#777' }}>
                      {card.rating || '4.5'}
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    width: 55,
                    height: 55,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '0.5rem',
                    background: 'transparent',
                  }}
                >
                  <img
                    src={getBankLogo(card.bank)}
                    alt={card.bank}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </div>

              <div style={dividerStyle} />
              <img
                src={card.cardImage}
                alt={card.creditcardname}
                style={{
                  width: '200px',
                  height: '177px',
                  margin: '0 auto',
                  borderRadius: '10px',
                  objectFit: 'contain',
                }}
              />

              <div
                style={{
                  padding: '1rem',
                  fontSize: isMobile ? '0.9rem' : '0.85rem',
                  color: '#444',
                  flexGrow: 1,
                }}
              >
                {isExpanded ? card.description : `${truncated}... `}
                {!isExpanded && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProtectedClick(card);
                    }}
                    style={{
                      color: '#1e3a8a',
                      fontWeight: 500,
                      cursor: 'pointer',
                      marginTop: '8px',
                    }}
                  >
                    Read More
                  </div>
                )}
              </div>

              <div style={{ padding: '0 1rem 1rem' }}>
                <p
                  style={{
                    fontWeight: 600,
                    fontSize: isMobile ? '0.85rem' : '0.8rem',
                    color: '#555',
                  }}
                >
                  Annual Fee
                </p>
                <p
                  style={{
                    fontWeight: 500,
                    fontSize: isMobile ? '0.9rem' : '0.85rem',
                  }}
                >
                  {card.annualFee}
                </p>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleProtectedClick(card);
                }}
                style={{
                  margin: '0 1rem 1rem',
                  padding: isMobile ? '1.1rem' : '0.8rem',
                  background: '#1e3a8a',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: '8px',
                  fontSize: isMobile ? '1.1rem' : undefined,
                }}
              >
                {card.buttonText || 'Apply Now'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Other Popular Banks */}
      <div
        style={{
          marginTop: '5rem',
          background: '#fff',
          padding: '3rem 2rem 0',
          borderRadius: '0',
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
        }}
      >
        <h2
          style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: '2rem' }}
        >
          Other Popular Banks
        </h2>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          {otherPopularBanks.map((b, idx) => (
            <div
              key={idx}
              onClick={() => goToBank(b)}
              style={{
                flex: '1 1 300px',
                borderRadius: '10px',
                background: '#fff',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                cursor: 'pointer',
                transition: '0.3s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow =
                  '0 12px 24px rgba(0,0,0,0.15)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)')
              }
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                }}
              >
                <h3 style={{ fontWeight: 600 }}>{b.name}</h3>
                <img
                  src={b.logo}
                  alt={b.name}
                  style={{ height: '30px', objectFit: 'contain' }}
                />
              </div>
              <p style={{ fontSize: '0.95rem', color: '#555' }}>
                {b.description}
              </p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '4rem' }}>
          <Consultation />
        </div>
      </div>
    </div>
  );
}
